
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { UserData } from '@/types/user';
import { debugLogger } from '@/utils/debugLogger';

// Cache simples com TTL
interface CacheEntry {
  data: UserData | null;
  timestamp: number;
  error?: string;
}

const profileCache = new Map<string, CacheEntry>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

// Helper function to validate if string is UUID
const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

const calculateAge = (birthDate: string): number => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
};

// Helper function to validate role compatibility
const canViewProfile = (
  currentUser: { role?: string; id: string },
  targetProfile: { role?: string; id: string }
) => {
  
  // Próprio perfil
  if (currentUser.id === targetProfile.id) {
    debugLogger.debug('SimpleUserProfile: Own profile access granted');
    return true;
  }
  
  // Compatibilidade de roles
  if (currentUser.role === 'sponsor' && targetProfile.role === 'sponsored') {
    debugLogger.debug('SimpleUserProfile: Sponsor viewing sponsored profile - access granted');
    return true;
  }
  if (currentUser.role === 'sponsored' && targetProfile.role === 'sponsor') {
    debugLogger.debug('SimpleUserProfile: Sponsored viewing sponsor profile - access granted');
    return true;
  }
  
  debugLogger.debug('SimpleUserProfile: Access denied due to role incompatibility', {
    currentUserRole: currentUser.role,
    targetProfileRole: targetProfile.role,
    currentUserId: currentUser.id,
    targetProfileId: targetProfile.id
  });
  return false;
};

export function useSimpleUserProfile(username: string, currentUserId?: string, skipRoleCheck: boolean = false) {
  const [profile, setProfile] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async (forceRefresh: boolean = false) => {
    if (!username) {
      setError('Username não fornecido');
      setLoading(false);
      return;
    }

    // Validação: não aceitar UUIDs como username
    if (isUUID(username)) {
      const errorMessage = 'Parâmetro inválido: UUID fornecido como username';
      debugLogger.error('SimpleUserProfile:', errorMessage, { providedValue: username });
      setError('Usuário não encontrado');
      setLoading(false);
      return;
    }

    // Verificar cache primeiro (se não forçar refresh e não estiver em bypass mode)
    const cacheKey = username.toLowerCase();
    if (!forceRefresh && !skipRoleCheck) {
      const cached = profileCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
        // Ignorar cache se tem erro de ROLE_INCOMPATIBLE e agora skipRoleCheck está ativo
        if (cached.error === 'ROLE_INCOMPATIBLE') {
          debugLogger.debug('SimpleUserProfile: Ignoring cached ROLE_INCOMPATIBLE, refetching');
        } else {
          debugLogger.debug('SimpleUserProfile: Using cached data for', username);
          setProfile(cached.data);
          setError(cached.error || null);
          setLoading(false);
          return;
        }
      }
    }

    try {
      setLoading(true);
      setError(null);
      
      debugLogger.debug('SimpleUserProfile: Fetching fresh data for username:', username);
      
      const { data, error: fetchError } = await supabase
        .from('profiles')
        .select('*')
        .ilike('username', username) // Case-insensitive
        .single();
      
      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          throw new Error('Usuário não encontrado');
        }
        throw new Error(`Erro ao buscar perfil: ${fetchError.message}`);
      }
      
      if (!data) {
        throw new Error('Usuário não encontrado');
      }

      // Mapear dados para o formato UserData esperado
      const mappedProfile: UserData = {
        id: data.id,
        username: data.username || `user_${data.id.slice(0, 8)}`,
        age: data.birth_date ? calculateAge(data.birth_date) : 0,
        bio: data.bio || '',
        gender: data.gender || '',
        orientation: data.orientation || '',
        role: data.role || '',
        city: data.city || '',
        state_code: data.state_code || '',
        occupation: data.occupation || '',
        education: data.education || '',
        interests: data.interests || [],
        relationship_type: data.relationship_type || '',
        income: data.income || '',
        share_income: data.share_income || false,
        sponsorship_value: data.sponsorship_value || undefined,
        main_photo_url: data.main_photo_url || '',
        photos: data.profile_photos || [],
        verification_level: data.verification_level || 'basic',
        hidden: data.hidden || false,
        onboarding_completed: data.onboarding_completed || false,
        location: data.city && data.state_code ? `${data.city}, ${data.state_code}` : (data.city || ''),
        joinedDate: data.created_at || '',
        isVerified: data.verification_level === 'full',
        // Physical characteristics
        height: data.height || '',
        body_type: data.body_type || '',
        hair_color: data.hair_color || '',
        eye_color: data.eye_color || '',
        // Habits and lifestyle
        smoker: data.smoker || '',
        drinker: data.drinker || '',
        piercings: data.piercings || '',
        tattoos: data.tattoos || '',
        // Relationship preferences
        marital_status: data.marital_status || '',
        sigilo_presenca: data.sigilo_presenca || '',
        travel_availability: data.travel_availability || '',
        children: data.children || '',
        // Bio AI analysis fields
        bio_score: data.bio_score || undefined,
        bio_topics: data.bio_topics || [],
        bio_last_analyzed: data.bio_last_analyzed || undefined
      };

      // Validar compatibilidade de roles se currentUserId for fornecido (e skipRoleCheck não estiver ativo)
      if (currentUserId && !skipRoleCheck) {
        const { data: currentUserData, error: currentUserError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', currentUserId)
          .single();

        if (currentUserError) {
          debugLogger.error('SimpleUserProfile: Error fetching current user data:', currentUserError);
        } else {
          const currentUser = {
            role: currentUserData.role,
            id: currentUserId
          };

          if (!canViewProfile(currentUser, { 
            role: mappedProfile.role,
            id: mappedProfile.id 
          })) {
            throw new Error('ROLE_INCOMPATIBLE');
          }
        }
      } else if (skipRoleCheck) {
        debugLogger.debug('SimpleUserProfile: Skipping role check (admin bypass)');
      }

      setProfile(mappedProfile);
      
      // Atualizar cache
      profileCache.set(cacheKey, {
        data: mappedProfile,
        timestamp: Date.now()
      });

      debugLogger.debug('SimpleUserProfile: Successfully fetched profile for', username, { userId: mappedProfile.id });
      
    } catch (err: any) {
      let errorMessage = 'Erro desconhecido ao carregar perfil';
      
      if (err.message === 'ROLE_INCOMPATIBLE') {
        // ROLE_INCOMPATIBLE é fluxo esperado, não erro técnico - usar INFO
        debugLogger.info('SimpleUserProfile: Role incompatibility detected', {
          username,
          message: 'User attempted to view incompatible profile role'
        });
        errorMessage = 'ROLE_INCOMPATIBLE';
      } else {
        // Erros reais continuam como ERROR
        debugLogger.error('SimpleUserProfile: Error fetching profile:', err);
        if (err.message) {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setProfile(null);
      
      // Cache do erro também (exceto para erros de compatibilidade de role)
      if (err.message !== 'ROLE_INCOMPATIBLE') {
        profileCache.set(cacheKey, {
          data: null,
          timestamp: Date.now(),
          error: errorMessage
        });
      }
    } finally {
      setLoading(false);
    }
  }, [username, currentUserId, skipRoleCheck]);

  // Fetch inicial apenas
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Função para refresh manual
  const refreshProfile = useCallback(() => {
    fetchProfile(true);
  }, [fetchProfile]);

  return { 
    profile,
    loading, 
    error,
    refreshProfile
  };
}
